import React, { useState, useEffect, useCallback } from 'react';
import { collection, collectionGroup, getDocs, query, limit, Timestamp, doc, setDoc, where } from 'firebase/firestore';
import { useFirebase } from '../../contexts/firebaseContext';
import { useSwornBuilderContext } from '../../contexts/SwornBuilderContext';
import { BuildData, FirebaseError } from '../../types';
import { X, RefreshCw, Eye, User, Globe, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';

interface AdminPanelProps {
  onClose: () => void;
}

interface EnrichedBuildData extends BuildData {
  _docId: string;
  _userId: string;
  _path: string;
  _isCommunity: boolean;
  _sharedDocId: string | null;
}

interface CustomGlobal {
  __app_id?: string;
}

const appId = (globalThis as unknown as CustomGlobal).__app_id || 'default-sworn-builder';

// Type guard for FirebaseError
const isFirebaseError = (error: unknown): error is FirebaseError => {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
};

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose }) => {
  const { db, userId } = useFirebase();
  const { handleLoadBuild } = useSwornBuilderContext();
  const [builds, setBuilds] = useState<EnrichedBuildData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Author Credit State
  const [showAuthorPrompt, setShowAuthorPrompt] = useState(false);
  const [authorNameInput, setAuthorNameInput] = useState('');
  const [selectedBuildForCommunity, setSelectedBuildForCommunity] = useState<EnrichedBuildData | null>(null);

  const fetchBuilds = useCallback(async () => {
    if (!db) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch user builds
      const buildsQuery = query(collectionGroup(db, 'builds'), limit(100)); // Limit to prevent explosion
      const snapshot = await getDocs(buildsQuery);
      
      // 2. Fetch shared docs once so we can map source build -> shared doc metadata.
      const sharedQuery = query(collection(db, 'artifacts', appId, 'shared_builds'), limit(500));
      const sharedSnapshot = await getDocs(sharedQuery);
      const sharedMap = new Map<string, { sharedDocId: string; isCommunityBuild: boolean }>();

      sharedSnapshot.forEach((sharedDoc) => {
        const sharedData = sharedDoc.data() as BuildData;
        const sourceId = sharedData.sourceBuildId || sharedData.id;
        const ownerId = sharedData.originalOwnerId;
        if (!sourceId || !ownerId) return;
        const key = `${ownerId}::${sourceId}`;
        sharedMap.set(key, {
          sharedDocId: sharedDoc.id,
          isCommunityBuild: Boolean(sharedData.isCommunityBuild)
        });
      });

      const fetchedBuilds: EnrichedBuildData[] = [];

      snapshot.forEach(doc => {
        const pathSegments = doc.ref.path.split('/');
        const usersIdx = pathSegments.indexOf('users');
        const pathUserId = (usersIdx !== -1 && usersIdx + 1 < pathSegments.length) 
          ? pathSegments[usersIdx + 1] 
          : 'unknown';
          
        const buildData = doc.data() as BuildData;
        const sharedKey = `${pathUserId}::${buildData.id || doc.id}`;
        const sharedMeta = sharedMap.get(sharedKey);
        fetchedBuilds.push({
          ...buildData,
          _docId: doc.id,
          _userId: pathUserId,
          _path: doc.ref.path,
          _isCommunity: sharedMeta?.isCommunityBuild ?? false,
          _sharedDocId: sharedMeta?.sharedDocId ?? null
        });
      });

      setBuilds(fetchedBuilds);
    } catch (err: unknown) {
      console.error("Admin Fetch Error:", err);
      if (isFirebaseError(err)) {
        if (err.code === 'permission-denied') {
          setError("Permission Denied. Your UID is likely not authorized in firestore.rules.");
        } else {
          setError(err.message || "Failed to fetch builds");
        }
      } else if (err instanceof Error) {
         setError(err.message);
      } else {
        setError("An unknown error occurred");
      }
    } finally {
      setLoading(false);
    }
  }, [db]);

    const executeCommunityToggle = useCallback(async (build: EnrichedBuildData, authorName?: string) => {
      if (!db) return;
      
      try {
        if (build._sharedDocId) {
          const sharedRef = doc(db, 'artifacts', appId, 'shared_builds', build._sharedDocId);
          if (build._isCommunity) {
            await setDoc(sharedRef, { isCommunityBuild: false }, { merge: true });
            setBuilds(prev => prev.map(b => b._docId === build._docId ? { ...b, _isCommunity: false } : b));
          } else {
            await setDoc(sharedRef, {
              isCommunityBuild: true,
              communityRank: 0,
              featuredAt: Timestamp.now(),
              author: authorName || build.author || 'Unknown'
            }, { merge: true });
            setBuilds(prev => prev.map(b => b._docId === build._docId ? { ...b, _isCommunity: true } : b));
          }
          } else {
          // First-time share snapshot for this user build, then mark as community.
              const { _docId, _userId, _path, _isCommunity, ...cleanData } = build;
          const shareId = crypto.randomUUID();
          const sharedRef = doc(db, 'artifacts', appId, 'shared_builds', shareId);
              
          const sharedData: BuildData = {
                  ...cleanData,
            id: shareId,
            sourceBuildId: build.id,
            originalOwnerId: build._userId,
            sharedAt: new Date().toISOString(),
                  isCommunityBuild: true,
            communityRank: 0,
            featuredAt: new Date().toISOString(),
            isSourceDeleted: false,
            author: authorName || build.author || 'Unknown'
              };

          await setDoc(sharedRef, {
            ...sharedData,
            sharedAt: Timestamp.now(),
            featuredAt: Timestamp.now()
          });
          setBuilds(prev => prev.map(b => b._docId === build._docId ? { ...b, _isCommunity: true, _sharedDocId: shareId } : b));
          }
      } catch (err: unknown) {
          console.error("Failed to toggle community status", err);
          if (isFirebaseError(err)) {
              alert(`Failed to update: ${err.message}`);
          } else if (err instanceof Error) {
              alert(`Error: ${err.message}`);
          } else {
              alert("Failed to update community status. Check console.");
          }
      }
  }, [db]);


  const handleCommunityClick = useCallback((build: EnrichedBuildData) => {
      if (build._isCommunity) {
          // If it is already in community, just toggle (remove) it
          executeCommunityToggle(build);
      } else {
          // If adding, show prompt for author credit
          setSelectedBuildForCommunity(build);
          setAuthorNameInput('');
          setShowAuthorPrompt(true);
      }
  }, [executeCommunityToggle]);

  const confirmAddToCommunity = useCallback(() => {
      if (selectedBuildForCommunity) {
          executeCommunityToggle(selectedBuildForCommunity, authorNameInput.trim() || undefined);
          setShowAuthorPrompt(false);
          setSelectedBuildForCommunity(null);
      }
  }, [selectedBuildForCommunity, authorNameInput, executeCommunityToggle]);

  useEffect(() => {
    fetchBuilds();
  }, [fetchBuilds]);

  const loadBuildIntoApp = (build: EnrichedBuildData) => {
    // We can just load the data directly
    const { _docId, _userId, _path, ...cleanBuildData } = build;
    handleLoadBuild(cleanBuildData);
    onClose();
  };

  // Group builds by User ID
  const groupedBuilds = builds.reduce((groups, build) => {
    const uid = build._userId;
    if (!groups[uid]) groups[uid] = [];
    groups[uid].push(build);
    return groups;
  }, {} as Record<string, EnrichedBuildData[]>);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-slate-900 border border-[#e6a84f] rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-slate-800 rounded-t-lg">
          <div className="flex items-center gap-4">
            <h2 className="text-[#e6a84f] text-xl font-bold font-serif">Admin: Global Build Inspector</h2>
            <code className="text-xs bg-black px-2 py-1 rounded text-gray-400">UID: {userId}</code>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={fetchBuilds} 
              className="p-2 hover:bg-slate-700 rounded text-[#e6a84f]"
              title="Refresh"
              aria-label="Refresh Builds"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-slate-700 rounded text-gray-400 hover:text-white"
              title="Close"
              aria-label="Close Admin Panel"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 custom-scrollbar">
          {error && (
            <div className="bg-red-500/20 text-red-200 p-4 rounded mb-4 border border-red-500/50">
              <strong>Error:</strong> {error}
              <div className="mt-2 text-sm text-gray-300">
                Ensure <code>firestore.rules</code> has the correct permissions.
              </div>
            </div>
          )}

          <div className="space-y-8 pb-8">
            {Object.entries(groupedBuilds).map(([uid, userBuilds]) => (
              <div key={uid} className="bg-slate-800 border-2 border-slate-700 rounded-xl overflow-hidden shadow-xl">
                {/* User Header - Distinct Section */}
                <div className="bg-slate-950/80 p-4 border-b border-slate-700 flex flex-wrap items-center gap-4">
                   <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                     <User size={20} className="text-indigo-400" />
                   </div>
                   <div className="flex flex-col min-w-[200px]">
                      <span className="text-[11px] text-slate-500 uppercase font-black tracking-widest leading-none mb-1">Owner UID</span>
                      <span className="font-mono text-base text-slate-200 select-all">{uid}</span>
                   </div>
                   
                   <div className="ml-auto bg-slate-900 px-4 py-1.5 rounded-full border border-slate-700 flex items-center gap-2 shadow-inner">
                     <span className="text-indigo-400 font-bold font-mono">{userBuilds.length}</span>
                     <span className="text-slate-500 text-xs font-bold uppercase tracking-wider">Created Builds</span>
                   </div>
                </div>
                
                {/* Builds Grid Area */}
                <div className="p-5 bg-slate-900/50">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-slate-600 rounded-full inline-block"></span>
                    Build Collection
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {userBuilds.map((build, idx) => (
                    <div key={build._docId || idx} className="bg-slate-900 border border-gray-800 p-3 rounded hover:border-[#e6a84f]/50 transition-colors group relative">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-white truncate pr-2 group-hover:text-[#e6a84f] transition-colors">{build.name || 'Unnamed'}</h3>
                            <span className="text-xs bg-slate-950 px-1.5 py-0.5 rounded text-gray-500 font-mono capitalize">
                                {build.knight || 'No Knight'}
                            </span>
                        </div>
                        
                        <div className="text-xs text-gray-400 space-y-1 mb-3">
                            <p>Updated: {formatDate(build.updatedAt)}</p>
                        </div>

                        <div className="flex gap-2">
                           <button 
                            onClick={() => handleCommunityClick(build)}
                            className={`flex-1 py-1.5 rounded border font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                                build._isCommunity 
                                ? 'bg-green-600 text-white border-green-500 hover:bg-red-600 hover:border-red-500' 
                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-green-600 hover:text-white'
                            }`}
                            title={build._isCommunity ? "Remove from Community" : "Add to Community"}
                           >
                                <Globe size={14} />
                           </button>
                           <button 
                            onClick={() => loadBuildIntoApp(build)}
                            className="flex-[3] py-1.5 bg-[#e6a84f]/10 text-[#e6a84f] border border-[#e6a84f]/30 rounded hover:bg-[#e6a84f] hover:text-black font-medium text-sm flex items-center justify-center gap-2 transition-all"
                           >
                                <Eye size={14} /> Inspect
                           </button>
                        </div>
                    </div>
                    ))}
                </div>
              </div>
            </div>
            ))}

            {!loading && builds.length === 0 && !error && (
               <div className="text-center py-10 text-gray-500">
                 No builds found (or permission denied silently)
               </div>
            )}
          </div>
        </div>
      </div>

      {/* Author Credit Modal */}
      {showAuthorPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="bg-slate-800 p-6 rounded-lg border border-[#e6a84f] shadow-2xl w-96 animate-in fade-in zoom-in duration-200">
                <h3 className="text-xl font-bold text-[#e6a84f] mb-2 font-serif">Credit the Author</h3>
                <p className="text-slate-400 text-sm mb-4">
                    Who created this build? Leave blank for "Unknown".
                </p>
                <input 
                    autoFocus
                    type="text" 
                    className="w-full bg-slate-950 text-white p-3 border border-slate-600 rounded mb-6 focus:border-[#e6a84f] outline-none"
                    placeholder="e.g. Sir Lancelot"
                    value={authorNameInput}
                    onChange={e => setAuthorNameInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && confirmAddToCommunity()}
                />
                <div className="flex justify-end gap-3">
                    <button 
                        onClick={() => setShowAuthorPrompt(false)} 
                        className="text-slate-400 hover:text-white px-4 py-2 font-medium"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={confirmAddToCommunity} 
                        className="bg-[#e6a84f] hover:bg-[#d6963d] text-black font-bold px-6 py-2 rounded shadow-lg transition-colors"
                    >
                        Add
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
